import ApartmentIcon from '@mui/icons-material/Apartment';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import {
  Avatar,
  Box,
  CircularProgress,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import CreateContract from './components/CreateContract';
import CreateServiceCenter from './components/CreateServiceCenter';
import CreateTeam from './components/CreateTeam';
import Dashboard from './components/Dashboard';
import Dataset from './components/Dataset';
import EditUser from './components/EditUser';
import { PartnerSafeApi } from './components/EnvFiles';
import Login from './components/Login';
import UserList from './components/UserList';
import UsersPanel from './components/UsersPanel';
import { RoleName } from './models/RoleName';
import { createAppTheme } from './theme';
import { getStoredToken } from "./utils/getStoredToken";
import { isTokenValid } from "./utils/isTokenValid";

const fetchUserData = async (token) => {
  try {
    const response = await axios.get(
      `${PartnerSafeApi}/api/v1/Contas/MeusDados`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (err) {
    console.error('Erro ao buscar dados do usuário:', err);
    return null;
  }
};

const hasDashboardPermission = (roles) => {
  return roles?.some((role) =>
    [
      RoleName.BACK_OFFICE,
      RoleName.GESTOR_DE_CENTRO_DE_SERVICO,
      RoleName.ADMINISTRADOR_DE_CONTRATO,
      RoleName.ADMIN,
      RoleName.ANALISTA_CTMO,
    ].includes(role.nome)
  );
};

const hasEditUserPermission = (roles) => {
  return roles?.some((role) =>
    [RoleName.BACK_OFFICE].includes(role.nome)
  );
};

const has6ROPermission = (roles) => {
  return roles?.some((role) =>
    [
      RoleName.BACK_OFFICE,
      RoleName.GESTOR_DE_CENTRO_DE_SERVICO,
      RoleName.ADMINISTRADOR_DE_CONTRATO,
      RoleName.ADMIN,
      RoleName.ANALISTA_CTMO
    ].includes(role.nome)
  );
};

const hasDatasetPermission = (roles) => {
  return roles?.some((role) => [RoleName.ADMIN].includes(role.nome));
};

const hasContractPermission = (roles) => {
  return roles?.some((role) => (
    [RoleName.BACK_OFFICE].includes(role.nome)
  ));
};

const hasServiceCentersPermission = (roles) => {
  return roles?.some((role) => (
    [RoleName.BACK_OFFICE].includes(role.nome)
  ));
};

const hasTeamsPermission = (roles) => {
  return roles?.some((role) => (
    [RoleName.BACK_OFFICE].includes(role.nome)
  ));
};

const Protected6RORoute = ({ element, roles, redirectPath = '/dashboard' }) => {
  const hasPermission = has6ROPermission(roles);

  return hasPermission ? element : <Navigate to={redirectPath} replace />;
};

const ProtectedDatasetRoute = ({ element, roles, redirectPath = '/dashboard' }) => {
  return hasDatasetPermission(roles) ? element : <Navigate to={redirectPath} replace />;
};

const ProtectedContractRoute = ({ element, roles, redirectPath = '/dashboard' }) => {
  return hasContractPermission(roles) ? element : <Navigate to={redirectPath} replace />;
};

const ProtectedServiceCentersRoute = ({ element, roles, redirectPath = '/dashboard' }) => {
  return hasContractPermission(roles) ? element : <Navigate to={redirectPath} replace />;
};

const ProtectedTeamsRoute = ({ element, roles, redirectPath = '/dashboard' }) => {
  return hasTeamsPermission(roles) ? element : <Navigate to={redirectPath} replace />;
};


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const token = getStoredToken();

    const validateAndFetchUser = async () => {
      if (token && isTokenValid(token)) {
        const userData = await fetchUserData(token);

        if (userData) {
          setIsAuthenticated(true);
          setUserName(userData.nome || '');
          setUserEmail(userData.email || '');
          setUserRoles(userData.roles || []);

          localStorage.setItem('partnersafe_ls', JSON.stringify({
            nomeUsuario: userData.nome,
            email: userData.email,
            token,
          }));
        }
      }
    };

    validateAndFetchUser();
  }, []);

  const handleLoginSuccess = async (email, token) => {
    setIsAuthenticated(true);

    const userData = await fetchUserData(token);

    if (userData) {
      setUserName(userData.nome);
      setUserEmail(email || '');
      setUserRoles(userData.roles || []);

      localStorage.setItem('partnersafe_ls', JSON.stringify({
        nomeUsuario: userData?.nome || '',
        email: email || '',
        token
      }));
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserName('');
    setUserEmail('');

    localStorage.removeItem('partnersafe_ls');
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/register"
          element={
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
              <CssBaseline />
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 4,
                  letterSpacing: '-0.02em',
                }}
              >
                Cadastro de Usuário
              </Typography>
              <UsersPanel />
            </Box>
          }
        />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <AppContent
                userName={userName}
                userEmail={userEmail}
                userRoles={userRoles}
                selectedUser={selectedUser}
                onLogout={handleLogout}
                changeSelectedUser={setSelectedUser}
              />
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
      </Routes>
    </Router>
  );
}

function AppContent({
  userName,
  userEmail,
  onLogout,
  userRoles,
  selectedUser,
  changeSelectedUser,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [navigation, setNavigation] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [themeMode, setThemeMode] = useState('light');

  const theme = createAppTheme(themeMode);

  const handleThemeToggle = () => {
    setThemeMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.split(' ');
    const initials = nameParts.length > 1
      ? nameParts[0][0] + nameParts[nameParts.length - 1][0]
      : nameParts[0][0];
    return initials.toUpperCase();
  };

  const checkUserRules = useCallback(() => {
    const currentNavigation = [];

    // Mostar Dashboad para: Admin, BackOffice, AdministradorDeContrato
    if (hasDashboardPermission(userRoles)) {
      const dashboardChildren = [];

      // Mostar 6RO para: Admin, BackOffice, AdministradorDeContrato
      if (has6ROPermission(userRoles)) {
        dashboardChildren.push({
          segment: '6ro',
          title: '6RO',
          icon: <DescriptionIcon />,
        });
      }

      // Mostar Dataset para: Admin
      if (hasDatasetPermission(userRoles)) {
        dashboardChildren.push({
          segment: 'dataset',
          title: 'Dataset',
          icon: <DescriptionIcon />,
        });
      }

      currentNavigation.push({
        segment: 'dashboard',
        title: 'Dashboard',
        icon: <DashboardIcon />,
        children: dashboardChildren,
      });
    }

    // Mostar Painel de Usuário para: Admin, BackOffice, AdministradorDeContrato
    if (hasEditUserPermission(userRoles)) {
      currentNavigation.push({
        segment: 'users-panel',
        title: 'Painel de Usuários',
        icon: <PeopleIcon />,
        children: [
          {
            segment: 'management',
            title: 'Gestão de Usuários',
            icon: <PeopleIcon />,
          },
        ],
      });
    }

    // Mostar Contratos para: Admin, BackOffice, AdministradorDeContrato
    if (hasContractPermission(userRoles)) {
      currentNavigation.push({
        segment: 'contracts',
        title: 'Contratos',
        icon: <DescriptionIcon />,
        children: [
          {
            segment: 'register',
            title: 'Cadastrar Contrato',
            icon: <DescriptionIcon />,
          },
        ],
      });
    }

    // Mostar Centros de Serviços para: Admin, BackOffice, AdministradorDeContrato
    if (hasServiceCentersPermission(userRoles)) {
      currentNavigation.push({
        segment: 'service-centers',
        title: 'Centro de Serviço',
        icon: <ApartmentIcon />,
        children: [
          {
            segment: 'register',
            title: 'Cadastrar Centro',
            icon: <ApartmentIcon />,
          },
        ],
      });

      // Mostar Equipes para: Admin, BackOffice, AdministradorDeContrato
        if (hasTeamsPermission(userRoles)) {
          currentNavigation.push({
            segment: 'teams',
            title: 'Equipes',
            icon: <PeopleIcon />,
            children: [
              {
                segment: 'register',
                title: 'Cadastrar Equipe',
                icon: <PeopleIcon />,
              },
            ],
          });
        }
    }    

    setNavigation(currentNavigation);
  }, [userRoles]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      await checkUserRules();
      setIsLoading(false);
    };
    loadInitialData();
  }, [checkUserRules]);

  const userInitials = getInitials(userName);

  const router = {
    pathname: location.pathname,
    searchParams: new URLSearchParams(location.search),
    navigate: (path) => navigate(path),
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppProvider
        navigation={navigation}
        router={router}
        theme={theme}
        branding={{
          logo: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                PartnerSafe
              </Typography>
              <Tooltip title="Alternar tema">
                <IconButton
                  onClick={handleThemeToggle}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  {themeMode === 'dark' ? (
                    <Brightness7Icon />
                  ) : (
                    <Brightness4Icon />
                  )}
                </IconButton>
              </Tooltip>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  cursor: 'pointer',
                  width: 40,
                  height: 40,
                  boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                  '&:hover': {
                    boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
                  },
                }}
                onClick={handleAvatarClick}
              >
                {userInitials}
              </Avatar>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    background: theme.palette.mode === 'light'
                      ? '#FFFFFF'
                      : 'linear-gradient(145deg, #121212 0%, #1a1a1a 100%)',
                    boxShadow: theme.palette.mode === 'light'
                      ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
                      : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                    border: theme.palette.mode === 'light'
                      ? '1px solid rgba(0, 0, 0, 0.1)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                  },
                }}
              >
                <MenuItem onClick={handleMenuClose}>
                  <Typography variant="body2">{userName}</Typography>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <Typography variant="body2">{userEmail}</Typography>
                </MenuItem>
                <MenuItem onClick={() => {
                  handleMenuClose();
                  onLogout();
                }}>
                  <Typography variant="body2">Sair</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ),
        }}
      >
        <DashboardLayout hideNavigation={navigation.length === 0}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard roles={userRoles} />} />
            <Route
              path="/dashboard/6ro"
              element={
                <Protected6RORoute
                  roles={userRoles}
                  element={<Dashboard roles={userRoles} />}
                />
              }
            />
            <Route
              path="/dashboard/dataset"
              element={
                <ProtectedDatasetRoute
                  roles={userRoles}
                  element={<Dataset />}
                />
              }
            />
            <Route
              path="/users-panel/create"
              element={
                <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
                  <CssBaseline />
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 4,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Cadastro de Usuário
                  </Typography>
                  <UsersPanel />
                </Box>
              }
            />
            <Route
              path="/users-panel/management"
              element={<UserList changeSelectedUser={changeSelectedUser} />}
            />
            <Route
              path="/users-panel/edit"
              element={<EditUser selectedUser={selectedUser} />}
            />
            <Route
              path="/contracts/register"
              element={
                <ProtectedContractRoute
                  roles={userRoles}
                  element={<CreateContract />}
                />
              }
            />
            <Route
              path="/service-centers/register"
              element={
                <ProtectedServiceCentersRoute
                  roles={userRoles}
                  element={<CreateServiceCenter />}
                />
              }
            />
            <Route
              path="/teams/register"
              element={
                <ProtectedTeamsRoute
                  roles={userRoles}
                  element={<CreateTeam />}
                />
              }
            />
          </Routes>
        </DashboardLayout>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
